/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { HTMLAttributesSignalExcluded } from '../UNSAFE_attributeUtils';
import { LayoutInfo } from '../../UNSAFE_Legend/useLegendLayout';
import { dvtVars } from '../../Common/themes/themeContract.css';
import { xUnits } from '../../UNSAFE_Theme/utils';
import { Font } from '../../hooks/PRIVATE_useTextDimensions';
import { styles } from '../../UNSAFE_Legend/themes/LegendStyles.css';
import { ItemInfo, LegendItemData, LegendSectionData } from '../UNSAFE_visTypes/legend';
import { cancelEvent } from '../../utils/UNSAFE_visUtils/eventsUtils';

/**
 * Returns the value of the data-oj-item-index attribute for a given element.
 * @param element The HTML element.
 * @returns
 */
export const getItemInfo = (element: HTMLElement) => {
  const item = element.dataset['ojItem'];
  const section = element.dataset['ojSection'];

  if (item == null || section == null) {
    return;
  }
  return {
    itemIdx: Number(item),
    sectionIdx: Number(section)
  };
};

/**
 * Checks if the item is highlighted.
 * @param id Id of the item
 * @param highlighted The set of highlighted item
 */
export function isItemHighlighted<K>(id: K, highlighted?: K[]) {
  if (!highlighted) {
    return;
  }
  if (highlighted.length === 0) {
    return true;
  }
  return new Set(highlighted).has(id);
}

/**
 * Checks if the item is hidden.
 * @param id Id of the item.
 * @param hiddenIds The set of hidden items.
 * @returns
 */
export function isItemHidden<K>(id: K, hiddenIds?: K[]) {
  if (!hiddenIds) {
    return;
  }
  return new Set(hiddenIds).has(id);
}
/**
 * Checks if the item is the currentItem and if the focus ring
 * is visible.
 * @param itemInfo ItemInfo
 * @param itemIdx Item index
 * @param sectionIdx Section index
 */
export const hasFocusRing = (itemInfo: ItemInfo, itemIdx: number, sectionIdx?: number) => {
  if (!itemInfo.isFocusVisible) return false;
  const { itemIdx: item, sectionIdx: section } = itemInfo;

  if (sectionIdx == null) {
    return item === itemIdx;
  }
  return item === itemIdx && section === sectionIdx;
};

/**
 * Returns the text for the legend datatip.
 * @param items The legend items data.
 * @param itemInfoRef The item currently hovered.
 * @param currentItem The item that is currently keyboard focused.
 */
export function getDatatipText<K, D extends LegendItemData<K>>(
  items: LegendItemData<K>[] | LegendSectionData<K, D>[],
  getItem: (
    itemInfo: ItemInfo,
    items: LegendItemData<K>[] | LegendSectionData<K, D>[]
  ) => LegendItemData<K> | undefined,
  focusedItem: ItemInfo,
  hoveredItem?: ItemInfo
) {
  const isPointerActive = hoveredItem?.isCurrent;

  if (isPointerActive && hoveredItem.itemIdx != null) {
    return getItem(hoveredItem, items)?.datatip;
  }
  if (focusedItem && focusedItem.isCurrent) {
    return getItem(focusedItem, items)?.datatip;
  }
  return;
}

/**
 * Returns the aria properties for the legend.
 * @param isReadOnly { boolean } If the legend is readonly.
 * @param ariaLabel { string } The aria-label set by the app.
 * @returns The aria props.
 */
export function getAriaProps<K>(
  isReadOnly?: boolean,
  ariaLabel?: string,
  hiddenIds?: K[],
  ariaDescribedBy?: string,
  ariaLabelledBy?: string
): {
  'aria-label'?: HTMLAttributesSignalExcluded['aria-label'];
  'aria-disabled'?: HTMLAttributesSignalExcluded['aria-disabled'];
  'aria-describedby': HTMLAttributesSignalExcluded['aria-describedby'];
  'aria-labelledby': HTMLAttributesSignalExcluded['aria-labelledby'];
  role?: HTMLAttributesSignalExcluded['role'];
} {
  const hideAndShow = hiddenIds && !isReadOnly;
  return {
    'aria-label': ariaLabel,
    'aria-disabled': isReadOnly,
    role: hideAndShow ? 'menu' : 'application',
    'aria-describedby': ariaDescribedBy,
    'aria-labelledby': ariaLabelledBy
  };
}

/**
 * Returns the item props for BaseLegendItem.
 * @param item The legend item.
 * @param itemIdx The item index of the item.
 * @param sectionIdx The section index of section of the item.
 * @param focusedItem The info of currently focused item.
 * @param isReadOnly Whether the legend is readonly.
 * @param hoveredItem The info of the currently hovered item.
 * @param hiddenIds The hidden ids.
 * @param highlightedIds The highlighted ids.
 */
export function getItemProps<K>(
  item: LegendItemData<K>,
  itemIdx: number,
  sectionIdx: number | undefined,
  focusedItem: ItemInfo,
  isReadOnly?: boolean,
  hoveredItem?: ItemInfo,
  hiddenIds?: K[],
  highlightedIds?: K[]
) {
  let isCurrent = false;
  if (!isReadOnly) {
    const isFocusedItem = itemIdx === focusedItem.itemIdx && sectionIdx === focusedItem.sectionIdx;
    const isHoveredItem =
      itemIdx === hoveredItem?.itemIdx && sectionIdx === hoveredItem?.sectionIdx;
    isCurrent = !!(
      (isFocusedItem && focusedItem.isCurrent) ||
      (isHoveredItem && hoveredItem?.isCurrent)
    );
  }

  return {
    isCurrent,
    isHighlighted: isItemHighlighted<K>(item.id, highlightedIds),
    isHidden: isItemHidden(item.id, hiddenIds),
    isFocused: hasFocusRing(focusedItem, itemIdx, sectionIdx),
    key: item.id,
    ...item
  };
}

/**
 * Compares if two ItemInfo are equal
 * @param item1
 * @param item2
 * @returns
 */
export const isEqualItem = (item1?: ItemInfo, item2?: ItemInfo) => {
  return item1?.itemIdx === item2?.itemIdx && item1?.sectionIdx === item2?.sectionIdx;
};

/**
 * Returns the num of cols and rows for the legend.
 */
export const getGridNums = (numItems: number, layoutInfo?: LayoutInfo) => {
  let itemsPerRow;
  let itemsPerCol;

  if (layoutInfo?.isWrapped) {
    itemsPerRow = layoutInfo.itemsPerRow;
    itemsPerCol = Math.ceil(numItems / itemsPerRow);
    itemsPerRow = Math.ceil(numItems / itemsPerCol);
  }

  return { itemsPerRow, itemsPerCol };
};

/**
 * Returns the grid row and column for given item.
 * @param noOfItems The number of items in the section.
 * @param index The index of the item.
 * @param itemsPerRow The num of items per row in the grid.
 * @param itemsPerCol The num of items per col in the grid.
 * @returns
 */
export function getGridCellIndices(
  noOfItems: number,
  index: number,
  itemsPerRow?: number,
  itemsPerCol?: number
) {
  let gridRow;
  let gridCol;

  if (itemsPerRow && itemsPerCol && noOfItems > itemsPerRow) {
    gridRow = (index % itemsPerCol) + 1;
    gridCol = Math.floor(index / itemsPerCol) + 1;
  }
  return { gridRow, gridCol };
}

/**
 * Returns the resolved values for the legend vars.
 */

export const legendVars = [
  {
    tokenVar: xUnits(1),
    key: 'unit',
    cssProp: 'padding'
  },
  {
    tokenVar: dvtVars.label.sm.fontSize,
    key: 'fontSize',
    cssProp: 'font-size'
  }
];

/**
 * Returns the resolved values for the legend vars.
 */
export function getLegendTextStyle(props: Font, resolvedVars: Record<string, any>) {
  return {
    fontStyle: props.fontStyle,
    fontSize: (resolvedVars['fontSize'] ?? props.fontSize) as string | undefined,
    fontWeight: props.fontWeight,
    fontFamily: props.fontFamily
  };
}

/**
 * Returns the style for current legend item.
 */
export function getLegendCurrentItemStyle(
  isCurrent: boolean,
  isHighlighted: boolean | undefined,
  hideAndShowBehavior: 'on' | 'off',
  hoverBehavior: 'dim' | 'none',
  isHidden: boolean | undefined,
  actionable: 'inherit' | 'off'
) {
  if (isCurrent) {
    if (isHighlighted == null && isHidden == null && actionable !== 'off')
      return styles.legendItemIsCurrentWithCursor;
    else if (isHighlighted == null && hideAndShowBehavior === 'on' && actionable !== 'off')
      return styles.legendItemIsCurrentWithCursor;
    else if (isHidden == null && hoverBehavior === 'dim' && actionable !== 'off')
      return styles.legendItemIsCurrentWithoutCursor;
  }
  return '';
}

export function getIsLegendItemActionable(
  legendData: any[],
  isReadOnly: boolean,
  isSectional?: boolean
) {
  return (itemInfo: ItemInfo) => {
    let item;
    if (isSectional && itemInfo.sectionIdx != null) {
      item = legendData[itemInfo.sectionIdx].items[itemInfo.itemIdx];
    } else {
      item = legendData[itemInfo.itemIdx];
    }
    return !isReadOnly && item?.actionable !== 'off';
  };
}
/*
 * Returns KeyUp handler for legend.
 * @param triggerOnAction
 * @param updatefocusedItemInfo
 * @param focusedItemInfo
 * @returns
 */
export function getKeyUpHandler(
  triggerOnAction: () => void,
  updatefocusedItemInfo: (itemInfo: ItemInfo) => void,
  focusedItemInfo: ItemInfo,
  isHideShow?: boolean
) {
  return (event: KeyboardEvent) => {
    const key = event.code;
    switch (key) {
      case 'Space': {
        if (isHideShow) {
          triggerOnAction();
          cancelEvent(event);
        }
        break;
      }
      case 'Enter': {
        triggerOnAction();
        cancelEvent(event);
        break;
      }
      case 'Home': {
        updatefocusedItemInfo({
          itemIdx: 0,
          sectionIdx: 0,
          isFocusVisible: true,
          isCurrent: true
        });
        cancelEvent(event);
        break;
      }
      case 'End': {
        updatefocusedItemInfo({
          itemIdx: -1,
          sectionIdx: -1,
          isCurrent: true,
          isFocusVisible: true
        });
        cancelEvent(event);
        break;
      }
      case 'Tab': {
        updatefocusedItemInfo({
          ...focusedItemInfo,
          isCurrent: true,
          isFocusVisible: true
        });
        cancelEvent(event);
        break;
      }
    }
  };
}
